<template>
  <section data-test="termForm">
    <SchemaForm
      :value="value"
      :fields="termFields.slice(0, 2)"
      :columns="2"
      :validation="true"
      :disabled="!editable"
      dirtyOn="blur"
      @input="update"
      v-on="$listeners"
    />
    <SchemaForm
      :value="value"
      :fields="termFields.slice(2, 4)"
      :columns="2"
      :validation="true"
      :disabled="!editable"
      dirtyOn="blur"
      @input="update"
      v-on="$listeners"
    />
    <SchemaForm
      :value="value"
      :fields="termFields.slice(4, 7)"
      :columns="2"
      :validation="true"
      :disabled="!editable"
      dirtyOn="blur"
      @input="update"
      v-on="$listeners"
    />
    <SchemaForm
      :value="value"
      :fields="termFields.slice(7)"
      :validation="true"
      :disabled="!editable"
      dirtyOn="blur"
      @input="update"
      v-on="$listeners"
    />
  </section>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { getPhases } from '@/api'
import SchemaForm from '@/components/Forms/Form'
import FormYesNo from '@/components/Forms/FormYesNo'
import FormText from '@/components/Forms/FormText'
import FormDate from '@/components/Forms/FormDate'
import FormMixin from '@/components/Forms/FormMixin'
import FormSelect from '@/components/Forms/FormSelect'

export default {
  mixins: [ FormMixin ],
  components: { SchemaForm },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    phases: [],
  }),
  computed: {
    ...mapGetters([ 'termDisplayName', 'semesterNames', 'school' ]),
    years () {
      return Array(5)
        .fill(null)
        .map((_, index) => {
          // Starting with a previous year
          const year = moment().add(index - 1, 'year')
          return `${year.format('YYYY')}-${year.add(1, 'year').format('YY')}`
        })
    },
    defaultTermFields () {
      return [
        {
          name: 'semester',
          label: 'Semester',
          type: FormSelect,
          config: { options: Object.keys(this.semesterNames) },
          customLabel: this.getSemesterName,
        },
        {
          name: 'year',
          label: 'Year',
          type: FormSelect,
          config: { options: this.years },
        },
        {
          name: 'startDate',
          label: 'Start Date',
          type: FormDate,
          required: true,
        },
        {
          name: 'endDate',
          label: 'End Date',
          type: FormDate,
          required: true,
        },
        {
          name: 'effectiveStartDate',
          label: 'Effective Start Date',
          type: FormDate,
          required: false,
        },
        {
          name: 'effectiveEndDate',
          label: 'Effective End Date',
          type: FormDate,
          required: false,
        },
        {
          name: 'censusEnrollDate',
          label: 'Census Date',
          type: FormDate,
          required: false,
        },
        {
          name: 'examPeriodStartDate',
          label: 'Exam Period Start Date',
          type: FormDate,
          placeholder: 'Set Exam Period Start Date',
        },
        {
          name: 'examPeriodEndDate',
          label: 'Exam Period End Date',
          type: FormDate,
          placeholder: 'Set Exam Period End Date',
          config: {
            disabledDates: {
              to: moment(this.value.examPeriodStartDate).toDate(),
            },
          },
        },
        {
          name: 'displayName',
          label: 'Display Name',
          placeholder: this.termDisplayName(this.value),
          type: FormText,
        },
        {
          name: 'termCode',
          label: 'Term Code',
          type: FormText,
        },
        {
          name: 'phaseId',
          label: 'Phase',
          type: FormSelect,
          clearable: true,
          placeholder: 'No phase',
          config: {
            options: this.phasesIds,
          },
          listeners: {
            clear: this.clearPhase,
          },
          customLabel: this.getPhaseName,
        },
        {
          name: 'historical',
          label: 'Historical',
          type: FormYesNo,
        },
      ]
    },
    termFields () {
      return this.defaultTermFields
        .map(defaultField => ({
          ...defaultField,
          ...(this.fields.find(field => field.name === defaultField.name) || {}),
        }))
        .filter(field => !field._exclude)
    },
    phasesIds () {
      return this.phases.map(({ id }) => id)
    },
  },
  created () {
    this.fetchPhases()
  },
  methods: {
    async fetchPhases () {
      const response = await getPhases({ school: this.school })
      if (response instanceof Error) throw response
      if (response.error instanceof Error) throw response.error
      this.phases = response.data
    },
    getSemesterName (semester) {
      return this.semesterNames[semester] || semester
    },
    getPhaseName (phaseId) {
      return (this.phases.find(({ id }) => phaseId === id) || {}).name
    },
    clearPhase () {
      this.$emit('input', { ...this.value, phaseId: undefined })
    },
  },
}
</script>
